import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { db } from "firebase-utils/firebase";
import type { Beverage, Org, SpecialItem } from "models";
import { CategoryType } from "models";
import { collection, doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { useAuth } from "contexts/authContext";
import { firestoreConverter } from "firebase-utils/firebase-data-converter";

interface OrgContextProps {
  currentOrg: Org | null;
  orgs: Array<Org>;
  beverages: Array<Beverage>;
  specialItems: Map<CategoryType, Array<SpecialItem>>;
  setCurrentOrg: (org: Org) => void;
  updateBeverageOptimistic: (id: string, updates: Partial<Beverage>) => Promise<void>;
  updateSpecialItemOptimistic: (id: string, updates: Partial<SpecialItem>) => Promise<void>;
}

const DEFAULT_CONTEXT: OrgContextProps = {
  currentOrg: null,
  orgs: [],
  beverages: [],
  specialItems: new Map(),
  setCurrentOrg: () => {},
  updateBeverageOptimistic: async () => {},
  updateSpecialItemOptimistic: async () => {},
};

const OrgContext = createContext<OrgContextProps>(DEFAULT_CONTEXT);

export function useOrg() {
  return useContext(OrgContext);
}

export const OrgProvider = ({ children }: { children: ReactNode }) => {

  const userOrgs = useAuth().orgs;

  const [currentOrg, setCurrentOrg] = useState<Org | null>(null);
  const [orgs, setOrgs] = useState<Array<Org>>([]);
  const [beverages, setBeverages] = useState<Array<Beverage>>([]);
  const [specialItems, setSpecialItems] = useState<Map<CategoryType, Array<SpecialItem>>>(new Map());

  useEffect(() => {
    const fetchOrgs = async () => {
      if (userOrgs && userOrgs.length > 0) {
        const orgsToSet = await Promise.all(
          userOrgs?.map(async (userOrg) => {
            const orgRef = doc(db, "orgs", userOrg).withConverter(firestoreConverter<Org>());
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              return orgSnap.data();
            }
            return null;
          })
        );
        const filteredOrgs = orgsToSet.filter((org): org is Org => org !== null);
        setOrgs(filteredOrgs);
        setCurrentOrg(filteredOrgs[0]);
      }
    };

    fetchOrgs();
  }, [userOrgs]);

  useEffect(() => {
    if (!currentOrg) {
      // If there's no currentOrg, clear out existing items
      setBeverages([]);
      setSpecialItems(new Map());
      return;
    }

    const beveragesRef = collection(db, `orgs/${currentOrg.id}/beverages`)
      .withConverter(firestoreConverter<Beverage>());
    const specialItemsRef = collection(db, `orgs/${currentOrg.id}/special-items`)
      .withConverter(firestoreConverter<SpecialItem>());
    
    const unsubscribeBeverages = onSnapshot(beveragesRef, (snapshot) => {
      setBeverages(snapshot.docs.map((doc) => doc.data()));
    });

    const unsubscribeSpecialitems = onSnapshot(specialItemsRef, (snapshot) => {
      const itemsMap = new Map<CategoryType, Array<SpecialItem>>();
      snapshot.docs.forEach((doc) => {
        const specialItem = doc.data();
        const items = itemsMap.get(specialItem.category.type) ?? new Array<SpecialItem>();
        items.push(specialItem);
        itemsMap.set(specialItem.category.type, items);
      });
      setSpecialItems(itemsMap);
    });

    return () => {
      unsubscribeBeverages();
      unsubscribeSpecialitems();
    };
  }, [currentOrg]);

  const updateBeverageOptimistic = async (id: string, updates: Partial<Beverage>): Promise<void> => {
    const previous = beverages;
    setBeverages((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
    try {
      await updateDoc(doc(db, `orgs/${currentOrg!.id}/beverages`, id), updates as Record<string, unknown>);
    } catch (error) {
      setBeverages(previous);
      throw error;
    }
  };

  const updateSpecialItemOptimistic = async (id: string, updates: Partial<SpecialItem>): Promise<void> => {
    const previous = specialItems;
    setSpecialItems((prev) => {
      const next = new Map(prev);
      for (const [type, items] of next) {
        next.set(type, items.map((item) => (item.id === id ? { ...item, ...updates } : item)));
      }
      return next;
    });
    try {
      await updateDoc(doc(db, `orgs/${currentOrg!.id}/special-items`, id), updates as Record<string, unknown>);
    } catch (error) {
      setSpecialItems(previous);
      throw error;
    }
  };

  return (
    <OrgContext.Provider value={{ currentOrg, orgs, beverages, specialItems, setCurrentOrg, updateBeverageOptimistic, updateSpecialItemOptimistic }}>
      {children}
    </OrgContext.Provider>
  );
};
